import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Download, Eye, Edit, Bold, Italic, Code, Link, Image, List, Quote, Table } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const MarkdownEditorTool: React.FC = () => {
  const { t } = useTranslation();
  const [markdown, setMarkdown] = useState(`# Welcome to Markdown Editor

## Features
- **Bold text** and *italic text*
- [Links](https://example.com)
- \`inline code\` and code blocks
- Lists and tables
- And much more!

### Code Example
\`\`\`javascript
function hello() {
  console.log("Hello, World!");
}
\`\`\`

### Table Example
| Name | Age | City |
|------|-----|------|
| John | 25  | NYC  |
| Jane | 30  | LA   |

> This is a blockquote
`);
  const [view, setView] = useState<'split' | 'edit' | 'preview'>('split');

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const downloadMarkdown = () => {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = 'document.md';
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  const insertMarkdown = (before: string, after: string = '', placeholder: string = '') => {
    const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = markdown.substring(start, end);
    const textToInsert = selectedText || placeholder;

    const newText = markdown.substring(0, start) + before + textToInsert + after + markdown.substring(end);
    setMarkdown(newText);

    // Set cursor position
    setTimeout(() => {
      const newCursorPos = start + before.length + textToInsert.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  const insertHeading = (level: number) => {
    const prefix = '#'.repeat(level) + ' ';
    insertMarkdown(prefix, '', 'Heading');
  };

  const insertBold = () => insertMarkdown('**', '**', 'bold text');
  const insertItalic = () => insertMarkdown('*', '*', 'italic text');
  const insertCode = () => insertMarkdown('`', '`', 'code');
  const insertCodeBlock = () => insertMarkdown('```\n', '\n```', 'code block');
  const insertLink = () => insertMarkdown('[', '](url)', 'link text');
  const insertImage = () => insertMarkdown('![', '](image-url)', 'alt text');
  const insertList = () => insertMarkdown('- ', '', 'list item');
  const insertNumberedList = () => insertMarkdown('1. ', '', 'list item');
  const insertTable = () => {
    const table = `| Header 1 | Header 2 | Header 3 |
|----------|----------|----------|
| Cell 1   | Cell 2   | Cell 3   |
| Cell 4   | Cell 5   | Cell 6   |`;
    insertMarkdown(table, '');
  };
  const insertQuote = () => insertMarkdown('> ', '', 'quote text');

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('tools.markdownEditor.name')}</span>
            <div className="flex gap-2">
              <Button
                onClick={() => setView('edit')}
                variant={view === 'edit' ? 'primary' : 'outline'}
                size="sm"
              >
                <Edit className="w-3 h-3 mr-1" />
                {t('tools.markdownEditor.edit')}
              </Button>
              <Button
                onClick={() => setView('split')}
                variant={view === 'split' ? 'primary' : 'outline'}
                size="sm"
              >
                {t('tools.markdownEditor.split')}
              </Button>
              <Button
                onClick={() => setView('preview')}
                variant={view === 'preview' ? 'primary' : 'outline'}
                size="sm"
              >
                <Eye className="w-3 h-3 mr-1" />
                {t('tools.markdownEditor.preview')}
              </Button>
              <Button
                onClick={() => copyToClipboard(markdown)}
                variant="outline"
                size="sm"
              >
                <Copy className="w-3 h-3 mr-1" />
                {t('common.copy')}
              </Button>
              <Button
                onClick={downloadMarkdown}
                variant="outline"
                size="sm"
              >
                <Download className="w-3 h-3 mr-1" />
                {t('common.download')}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`grid gap-4 ${view === 'split' ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {(view === 'edit' || view === 'split') && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.markdownEditor.editor')}
                </h3>

                {/* Markdown Toolbar */}
                <div className="mb-3 p-2 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800">
                  <div className="flex flex-wrap gap-1">
                    <Button onClick={() => insertHeading(1)} size="sm" variant="outline" title="Heading 1">
                      H1
                    </Button>
                    <Button onClick={() => insertHeading(2)} size="sm" variant="outline" title="Heading 2">
                      H2
                    </Button>
                    <Button onClick={() => insertHeading(3)} size="sm" variant="outline" title="Heading 3">
                      H3
                    </Button>
                    <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                    <Button onClick={insertBold} size="sm" variant="outline" title="Bold">
                      <Bold className="w-3 h-3" />
                    </Button>
                    <Button onClick={insertItalic} size="sm" variant="outline" title="Italic">
                      <Italic className="w-3 h-3" />
                    </Button>
                    <Button onClick={insertCode} size="sm" variant="outline" title="Inline Code">
                      <Code className="w-3 h-3" />
                    </Button>
                    <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                    <Button onClick={insertLink} size="sm" variant="outline" title="Link">
                      <Link className="w-3 h-3" />
                    </Button>
                    <Button onClick={insertImage} size="sm" variant="outline" title="Image">
                      <Image className="w-3 h-3" />
                    </Button>
                    <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                    <Button onClick={insertList} size="sm" variant="outline" title="Bullet List">
                      <List className="w-3 h-3" />
                    </Button>
                    <Button onClick={insertNumberedList} size="sm" variant="outline" title="Numbered List">
                      1.
                    </Button>
                    <Button onClick={insertQuote} size="sm" variant="outline" title="Quote">
                      <Quote className="w-3 h-3" />
                    </Button>
                    <Button onClick={insertTable} size="sm" variant="outline" title="Table">
                      <Table className="w-3 h-3" />
                    </Button>
                    <Button onClick={insertCodeBlock} size="sm" variant="outline" title="Code Block">
                      {'{}'}
                    </Button>
                  </div>
                </div>

                <Textarea
                  value={markdown}
                  onChange={(e) => setMarkdown(e.target.value)}
                  className="min-h-[500px] font-mono text-sm"
                  placeholder={t('tools.markdownEditor.placeholder')}
                />
              </div>
            )}
            
            {(view === 'preview' || view === 'split') && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.markdownEditor.preview')}
                </h3>
                <div className="min-h-[500px] p-4 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 overflow-auto">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    className="prose dark:prose-invert max-w-none"
                  >
                    {markdown}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
